import { AuthApi, TokenPair } from "@virtualprogaming/js-auth-sdk";
import { useEffect, useMemo } from "react";
import create from "zustand";

import { environment } from "~/environment";

const ACCESS_KEY = "AUTH_ACCESS_KEY";
const REFRESH_KEY = "AUTH_REFRESH_KEY";

export interface TokenClaims {
  user_id: number;
  username: string;
  refresh: boolean;
  is_staff: boolean;
  aud: string;
  iss: string;
  exp: number;
  iat: number;
  sub: string;
}

function parseToken(token: string): TokenClaims {
  return JSON.parse(atob(token.split(".")[1]));
}

function persistTokens(tokens: Partial<TokenPair>) {
  if (tokens.access) {
    localStorage.setItem(ACCESS_KEY, tokens.access);
  } else {
    localStorage.removeItem(ACCESS_KEY);
  }
  if (tokens.refresh) {
    localStorage.setItem(REFRESH_KEY, tokens.refresh);
  } else {
    localStorage.removeItem(REFRESH_KEY);
  }
}
const getAccessToken = () => localStorage.getItem(ACCESS_KEY) || undefined;
const getRefreshToken = () => localStorage.getItem(REFRESH_KEY) || undefined;

export function useAuthApi(): AuthApi {
  return useMemo(() => new AuthApi(undefined, environment.API_PREFIX), []);
}

export interface AuthState {
  access?: string;
  refresh?: string;
  setTokens: (tokens: Partial<TokenPair>) => void;
}

export const useAuth = create<AuthState>((set) => ({
  access: getAccessToken(),
  refresh: getRefreshToken(),
  setTokens: (tokens: Partial<TokenPair>) => {
    persistTokens(tokens);
    set(tokens);
  },
}));

const accessSelector = (s: AuthState) => s.access;
const setTokensSelector = (s: AuthState) => s.setTokens;

export function useCurrentUser(): TokenClaims | undefined {
  const access = useAuth(accessSelector);
  return useMemo(() => (access ? parseToken(access) : undefined), [access]);
}

export function useIsLoggedIn(): boolean {
  const access = useAuth(accessSelector);
  return useMemo(() => !!access, [access]);
}

export function useSetAuthTokens(): AuthState["setTokens"] {
  return useAuth(setTokensSelector);
}

export function useAuthStorageEffect() {
  const setTokens = useSetAuthTokens();
  useEffect(() => {
    const storageListener = () =>
      setTokens({ access: getAccessToken(), refresh: getRefreshToken() });
    window.addEventListener("storage", storageListener);
    return () => window.removeEventListener("storage", storageListener);
  });
}
